<!--
/**
 * OrangeHRM is a comprehensive Human Resource Management (HRM) System that captures
 * all the essential functionalities required for any enterprise.
 * Copyright (C) 2006 OrangeHRM Inc., http://www.orangehrm.com
 *
 * OrangeHRM is free software; you can redistribute it and/or modify it under the terms of
 * the GNU General Public License as published by the Free Software Foundation; either
 * version 2 of the License, or (at your option) any later version.
 *
 * OrangeHRM is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
 * without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
 * See the GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License along with this program;
 * if not, write to the Free Software Foundation, Inc., 51 Franklin Street, Fifth Floor,
 * Boston, MA  02110-1301, USA
 */
 -->

<template>
    <div>
        <save-education
            v-if="showSaveModal"
            :http="http"
            :api="educationEndpoint"
            @close="onSaveModalClose"
        ></save-education>
        <edit-education
            v-if="showEditModal"
            :http="http"
            :data="editModalState"
            @close="onEditModalClose"
        ></edit-education>
        <div class="orangehrm-horizontal-padding orangehrm-vertical-padding">
            <profile-action-header @click="onClickAdd">
                {{ $t('general.education') }}
            </profile-action-header>
        </div>
        <table-header
            :selected="checkedItems.length"
            :total="total"
            :loading="isLoading"
            @delete="onClickDeleteSelected"
        ></table-header>
        <div class="orangehrm-container">
            <oxd-card-table
                v-model:selected="checkedItems"
                :headers="headers"
                :items="items?.data"
                :selectable="true"
                :clickable="false"
                :loading="isLoading"
                :disabled="isDisabled"
                row-decorator="oxd-table-decorator-card"
            />
        </div>
        <div v-if="showPaginator" class="orangehrm-bottom-container">
            <oxd-pagination v-model:current="currentPage" :length="pages" />
        </div>
        <delete-confirmation ref="deleteDialog"></delete-confirmation>
    </div>
</template>

<script>
import usePaginate from '@ohrm/core/util/composable/usePaginate';
import {APIService} from '@ohrm/core/util/services/api.service';
import ProfileActionHeader from '@/orangehrmPimPlugin/components/ProfileActionHeader';
import SaveEducation from '@/orangehrmPimPlugin/components/SaveEducation';
import EditEducation from '@/orangehrmPimPlugin/components/EditEducation';
import DeleteConfirmationDialog from '@ohrm/components/dialogs/DeleteConfirmationDialog';

const educationNormalizer = (data) => {
    return data.map((item) => {
        return {
            id: item.id,
            level: item.education.name,
            year: item.year,
            score: item.score,
        };
    });
};

export default {
    name: 'EmployeeEducation',

    components: {
        'profile-action-header': ProfileActionHeader,
        'save-education': SaveEducation,
        'edit-education': EditEducation,
        'delete-confirmation': DeleteConfirmationDialog,
    },

    props: {
        employeeId: {
            type: String,
            required: true,
        },
    },

    setup(props) {
        const http = new APIService(
            window.appGlobal.baseUrl,
            `/api/v2/pim/employees/${props.employeeId}/educations`,
        );

        const educationEndpoint = '/api/v2/admin/educations';

        const {
            showPaginator,
            currentPage,
            total,
            pages,
            pageSize,
            response,
            isLoading,
            execQuery,
        } = usePaginate(http, {
            normalizer: educationNormalizer,
            toastNoRecords: false,
        });
        return {
            http,
            showPaginator,
            currentPage,
            isLoading,
            total,
            pages,
            pageSize,
            execQuery,
            items: response,
            educationEndpoint,
        };
    },

    data() {
        return {
            headers: [
                {
                    name: 'level',
                    slot: 'title',
                    title: this.$t('general.level'),
                    style: {flex: 1},
                },
                {
                    name: 'year',
                    title: this.$t('general.year'),
                    style: {flex: 1},
                },
                {
                    name: 'score',
                    title: this.$t('pim.gpa_score'),
                    style: {flex: 1},
                },
                {
                    name: 'actions',
                    slot: 'action',
                    title: this.$t('general.actions'),
                    style: {'flex-basis': '10em'},
                    cellType: 'oxd-table-cell-actions',
                    cellConfig: {
                        delete: {
                            onClick: this.onClickDelete,
                            component: 'oxd-icon-button',
                            props: {
                                name: 'trash',
                            },
                        },
                        edit: {
                            onClick: this.onClickEdit,
                            props: {
                                name: 'pencil-fill',
                            },
                        },
                    },
                },
            ],
            checkedItems: [],
            showSaveModal: false,
            showEditModal: false,
            editModalState: null,
        };
    },

    computed: {
        isDisabled() {
            return this.showSaveModal || this.showEditModal;
        },
    },

    methods: {
        onClickDeleteSelected() {
            const ids = this.checkedItems.map((index) => {
                return this.items?.data[index].id;
            });
            this.$refs.deleteDialog.showDialog().then((confirmation) => {
                if (confirmation === 'ok') {
                    this.deleteItems(ids);
                }
            });
        },
        onClickDelete(item) {
            this.$refs.deleteDialog.showDialog().then((confirmation) => {
                if (confirmation === 'ok') {
                    this.deleteItems([item.id]);
                }
            });
        },
        deleteItems(items) {
            if (items instanceof Array) {
                this.isLoading = true;
                this.http
                    .deleteAll({
                        ids: items,
                    })
                    .then(() => {
                        return this.$toast.deleteSuccess();
                    })
                    .then(() => {
                        this.isLoading = false;
                        this.resetDataTable();
                    });
            }
        },
        async resetDataTable() {
            this.checkedItems = [];
            await this.execQuery();
        },
        onClickAdd() {
            this.showEditModal = false;
            this.editModalState = null;
            this.showSaveModal = true;
        },
        onClickEdit(item) {
            this.showSaveModal = false;
            this.editModalState = item;
            this.showEditModal = true;
        },
        onSaveModalClose() {
            this.showSaveModal = false;
            this.resetDataTable();
        },
        onEditModalClose() {
            this.showEditModal = false;
            this.editModalState = null;
            this.resetDataTable();
        },
    },
};
</script>
